import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import atelier5Service from '../services/Atelier5Service';
import dashboardService from '../services/dashboardService';
import useAnalysisStore from './useAnalysisStore';
import toast from 'react-hot-toast';
import i18n from '../i18n';

const serializeForSave = (synthesisData, analysisId) => {
  const currentData = synthesisData;
  const ecosystemMeasures = currentData.ecosystemMeasures || [];

  return {
    strategicScenarios: currentData.strategicScenarios.map((scenario) => ({
      id: scenario.id,
      name: scenario.name,
      customMeasures:
        scenario.customMeasures?.map((measure) => ({
          ...measure,
          temporalPosition: measure.temporalPosition || 'immediate',
          position: measure.position || { x: 0, y: 0 },
          source: measure.source || 'custom',
          scenarioId: scenario.id,
          updatedAt: new Date().toISOString(),
        })) || [],
      dreadedEventMeasures:
        scenario.dreadedEventMeasures?.map((measure) => ({
          ...measure,
          temporalPosition: measure.temporalPosition || 'immediate',
          position: measure.position || { x: 0, y: 0 },
          source: measure.source || 'atelier1',
          scenarioId: scenario.id,
          updatedAt: new Date().toISOString(),
        })) || [],
      ecosystemMeasures:
        scenario.ecosystemMeasures?.map((measure) => ({
          ...measure,
          temporalPosition: measure.temporalPosition || 'immediate',
          position: measure.position || { x: 0, y: 0 },
          source: measure.source || 'ecosystem',
          scenarioId: scenario.id,
          updatedAt: new Date().toISOString(),
        })) || [],
      riskTreatment: scenario.riskTreatment
        ? {
            ...scenario.riskTreatment,
            scenarioId: scenario.id,
            updatedAt: new Date().toISOString(),
          }
        : null,
      residualRisk: scenario.residualRisk
        ? {
            ...scenario.residualRisk,
            scenarioId: scenario.id,
            updatedAt: new Date().toISOString(),
          }
        : null,
      temporalEvolution: scenario.temporalEvolution
        ? {
            ...scenario.temporalEvolution,
            scenarioId: scenario.id,
            updatedAt: new Date().toISOString(),
          }
        : null,
      attackPathTreatments: scenario.attackPathTreatments || {},
      attackPathResidualRisks: scenario.attackPathResidualRisks || {},
    })),
    ecosystemMeasures: ecosystemMeasures.map((measure) => ({
      ...measure,
      analysisId: analysisId,
      updatedAt: new Date().toISOString(),
    })),
    lastUpdated: new Date().toISOString(),
    version: 'simplified-v1',
  };
};

const useAtelier5Store = create(
  subscribeWithSelector((set, get) => ({
    synthesisData: null,
    isLoading: false,
    error: null,
    isDataLoaded: false,
    hasUserModified: false,
    lastUpdated: null,
    dashboardData: null,
    analysisId: null,

    getRiskScenarios: () => {
      const { synthesisData } = get();
      if (!synthesisData) return [];
      const { strategicScenarios, operationalScenarios } = synthesisData;
      if (!strategicScenarios?.length) return [];

      const riskScenarios = [];
      const coveredPathIds = new Set();

      (operationalScenarios || []).forEach((op) => {
        const ss = strategicScenarios.find(s =>
          s.attackPaths?.some(ap => (typeof ap === 'string' ? ap : ap.id) === op.attackPathId)
        );
        if (!ss) return;

        const attackPath = ss.attackPaths?.find(ap =>
          (typeof ap === 'string' ? ap : ap.id) === op.attackPathId
        );
        coveredPathIds.add(op.attackPathId);

        riskScenarios.push({
          id: `RS-${ss.id}-${op.attackPathId}-${op.id}`,
          strategicScenarioId: ss.id,
          attackPathId: op.attackPathId,
          attackPathName: (typeof attackPath === 'string' ? attackPath : attackPath?.name) || op.attackPathId,
          operationalScenarioId: op.id,
          name: `${ss.name || ''} → ${op.name || ''}`,
          gravity: ss.gravity || 1,
          likelihood: op.scenarioVraisemblance || 1,
          riskLevel: (ss.gravity || 1) * (op.scenarioVraisemblance || 1),
          treatment: ss.attackPathTreatments?.[op.attackPathId] || ss.riskTreatment || null,
          residualRisk: ss.attackPathResidualRisks?.[op.attackPathId] || ss.residualRisk || null,
          strategicScenarioName: ss.name || '',
          operationalScenarioName: op.name || '',
          preliminary: false,
        });
      });

      // Preliminary attack paths (no operational scenario, manual likelihood)
      strategicScenarios.forEach((ss) => {
        (ss.attackPaths || []).forEach((ap) => {
          const apId = typeof ap === 'string' ? ap : ap.id;
          if (!apId || coveredPathIds.has(apId)) return;
          const preliminary = (typeof ap === 'object' && ap.preliminary?.active) ? ap.preliminary : null;
          if (!preliminary) return;

          riskScenarios.push({
            id: `RS-${ss.id}-${apId}-preliminary`,
            strategicScenarioId: ss.id,
            attackPathId: apId,
            attackPathName: (typeof ap === 'string' ? ap : ap?.name) || apId,
            operationalScenarioId: null,
            name: `${ss.name || ''} → ${ap?.name || ''} (${i18n.t('workshop5.preliminary.shortLabel', 'Étude préliminaire')})`,
            gravity: ss.gravity || 1,
            likelihood: preliminary.likelihood || 1,
            riskLevel: (ss.gravity || 1) * (preliminary.likelihood || 1),
            treatment: ss.attackPathTreatments?.[apId] || ss.riskTreatment || null,
            residualRisk: ss.attackPathResidualRisks?.[apId] || ss.residualRisk || null,
            strategicScenarioName: ss.name || '',
            operationalScenarioName: null,
            preliminary: true,
            preliminaryJustification: preliminary.justification || '',
          });
        });
      });

      return riskScenarios;
    },

    loadData: async (analysisId) => {
      const resolvedId = analysisId && typeof analysisId === 'string'
        ? analysisId
        : useAnalysisStore.getState().currentAnalysis?.id;
      if (!resolvedId) {
        set({ error: 'Analysis ID not found' });
        return;
      }
      set({ isLoading: true, error: null, analysisId: resolvedId });

      try {
        const response = await atelier5Service.loadWorkshop5WithAutoEco(resolvedId);
        const data = response.data || response;

        if (data && data.strategicScenarios) {
          const { strategicScenarios, operationalScenarios, ecosystemMeasures } = data;

          if (resolvedId !== get().analysisId) return;
          set({
            synthesisData: {
              strategicScenarios: strategicScenarios || [],
              operationalScenarios: operationalScenarios || [],
              ecosystemMeasures: ecosystemMeasures || [],
            },
            isDataLoaded: true,
            isLoading: false,
            hasUserModified: false,
          });
        } else {
          throw new Error(response.message || 'Failed to load Workshop 5 data');
        }
      } catch (error) {
        console.error('[useAtelier5Store] Error loading data:', error);
        set({ isLoading: false, error: error.message });
      }
    },

    saveAll: async () => {
      const analysisId = useAnalysisStore.getState().currentAnalysis?.id;
      if (!analysisId) {
        toast.error('Analysis ID not found');
        return { success: false };
      }

      const toastId = toast.loading('Sauvegarde en cours...');

      try {
        const { synthesisData } = get();
        if (!synthesisData) {
          toast.error('No data to save', { id: toastId });
          return { success: false };
        }

        const simplifiedData = serializeForSave(synthesisData, analysisId);

        const response = await atelier5Service.saveWorkshop5Data(analysisId, simplifiedData);

        if (response.success) {
          toast.success('Données essentielles sauvegardées avec succès', { id: toastId });
          set({ hasUserModified: false });
          return { success: true };
        } else {
          throw new Error(response.error || 'Failed to save data');
        }
      } catch (error) {
        console.error('[useAtelier5Store] Save error:', error);
        toast.error(`Erreur lors de la sauvegarde: ${error.message}`, { id: toastId });
        return { success: false, error: error.message };
      }
    },

    saveRiskScenarioTab: async () => {
      const { synthesisData } = get();
      const analysisId = useAnalysisStore.getState().currentAnalysis?.id;
      if (!analysisId || !synthesisData) return { success: false };

      const toastId = toast.loading(i18n.t('workshop5.savingRiskTab'));

      try {
        const tabData = {
          strategicScenarios: synthesisData.strategicScenarios.map((s) => ({
            id: s.id,
            name: s.name,
            customMeasures: s.customMeasures || [],
            dreadedEventMeasures: s.dreadedEventMeasures || [],
            ecosystemMeasures: s.ecosystemMeasures || [],
            attackPathTreatments: s.attackPathTreatments || {},
            attackPathResidualRisks: s.attackPathResidualRisks || {},
          })),
        };

        const response = await atelier5Service.saveRiskScenarioTabData(analysisId, tabData);

        if (response.success) {
          toast.success(i18n.t('workshop5.riskTabSaved'), { id: toastId });
          set({ hasUserModified: false });
          return { success: true };
        }
        throw new Error(response.error || 'Failed to save');
      } catch (error) {
        console.error('[useAtelier5Store] Tab save error:', error);
        toast.error(`Error: ${error.message}`, { id: toastId });
        return { success: false, error: error.message };
      }
    },

    silentSave: async () => {
      const { isDataLoaded, hasUserModified, synthesisData } = get();

      if (!isDataLoaded || !hasUserModified) return 'skipped';

      const analysisId = useAnalysisStore.getState().currentAnalysis?.id;
      if (!analysisId || !synthesisData) return false;

      const simplifiedData = serializeForSave(synthesisData, analysisId);

      const response = await atelier5Service.saveWorkshop5Data(analysisId, simplifiedData);
      if (response.success) {
        set({ hasUserModified: false });
        return true;
      }
      console.error('[useAtelier5Store] Silent save failed:', response.error);
      return false;
    },

    canAutoSave: () => {
      const { isDataLoaded, hasUserModified } = get();
      return isDataLoaded && hasUserModified;
    },

    markModified: () => {
      const { hasUserModified } = get();
      if (!hasUserModified) {
        set({ hasUserModified: true });
      }
    },

    clearModified: () => {
      set({ hasUserModified: false });
    },

    updateSynthesisData: (newData) => {
      set((state) => {
        const updated = {
          synthesisData: newData,
          lastUpdated: new Date().toISOString(),
        };
        if (state.isDataLoaded) {
          updated.hasUserModified = true;
        }
        return updated;
      });
    },

    updateScenario: (updatedScenario) => {
      set((state) => {
        if (!state.synthesisData) return state;

        const updatedStrategicScenarios = state.synthesisData.strategicScenarios.map((s) => {
          if (s.id === updatedScenario.id) {
            return {
              ...s,
              ...updatedScenario,
              customMeasures: updatedScenario.customMeasures || s.customMeasures || [],
              dreadedEventMeasures: updatedScenario.dreadedEventMeasures || s.dreadedEventMeasures || [],
              ecosystemMeasures: updatedScenario.ecosystemMeasures || s.ecosystemMeasures || [],
              temporalEvolution: updatedScenario.temporalEvolution || s.temporalEvolution,
            };
          }
          return s;
        });

        return {
          synthesisData: {
            ...state.synthesisData,
            strategicScenarios: updatedStrategicScenarios,
          },
          hasUserModified: state.isDataLoaded ? true : state.hasUserModified,
          lastUpdated: new Date().toISOString(),
        };
      });
    },

    addCustomMeasure: (scenario, customMeasure) => {
      const newCustomMeasure = {
        ...customMeasure,
        id:
          customMeasure.id ||
          `custom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        scenarioId: scenario.id,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      set((state) => {
        if (!state.synthesisData) return state;

        const updatedStrategicScenarios = state.synthesisData.strategicScenarios.map((s) => {
          if (s.id === scenario.id) {
            return {
              ...s,
              customMeasures: [...(s.customMeasures || []), newCustomMeasure],
            };
          }
          return s;
        });

        return {
          synthesisData: {
            ...state.synthesisData,
            strategicScenarios: updatedStrategicScenarios,
          },
          hasUserModified: state.isDataLoaded ? true : state.hasUserModified,
        };
      });

      return { success: true, data: newCustomMeasure };
    },

    addRiskTreatment: (scenario, riskTreatment) => {
      const newRiskTreatment = {
        ...riskTreatment,
        scenarioId: scenario.id,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      set((state) => {
        if (!state.synthesisData) return state;

        const updatedStrategicScenarios = state.synthesisData.strategicScenarios.map((s) => {
          if (s.id === scenario.id) {
            return {
              ...s,
              riskTreatment: newRiskTreatment,
            };
          }
          return s;
        });

        return {
          synthesisData: {
            ...state.synthesisData,
            strategicScenarios: updatedStrategicScenarios,
          },
          hasUserModified: state.isDataLoaded ? true : state.hasUserModified,
        };
      });

      return { success: true, data: newRiskTreatment };
    },

    addAttackPathTreatment: (strategicScenarioId, attackPathId, treatment) => {
      const newTreatment = {
        ...treatment,
        scenarioId: strategicScenarioId,
        updatedAt: new Date().toISOString(),
      };
      set((state) => {
        if (!state.synthesisData) return state;
        const updatedScenarios = state.synthesisData.strategicScenarios.map(s => {
          if (s.id !== strategicScenarioId) return s;
          return {
            ...s,
            attackPathTreatments: {
              ...(s.attackPathTreatments || {}),
              [attackPathId]: newTreatment,
            },
          };
        });
        return {
          synthesisData: { ...state.synthesisData, strategicScenarios: updatedScenarios },
          hasUserModified: state.isDataLoaded ? true : state.hasUserModified,
        };
      });
      return { success: true, data: newTreatment };
    },

    addAttackPathResidualRisk: (strategicScenarioId, attackPathId, residualRiskData) => {
      const newResidualRisk = {
        ...residualRiskData,
        scenarioId: strategicScenarioId,
        updatedAt: new Date().toISOString(),
      };
      set((state) => {
        if (!state.synthesisData) return state;
        const updatedScenarios = state.synthesisData.strategicScenarios.map(s => {
          if (s.id !== strategicScenarioId) return s;
          return {
            ...s,
            attackPathResidualRisks: {
              ...(s.attackPathResidualRisks || {}),
              [attackPathId]: newResidualRisk,
            },
          };
        });
        return {
          synthesisData: { ...state.synthesisData, strategicScenarios: updatedScenarios },
          hasUserModified: state.isDataLoaded ? true : state.hasUserModified,
        };
      });
      return { success: true, data: newResidualRisk };
    },

    addResidualRisk: (scenario, residualRiskData) => {
      const residualRisk = {
        scenarioId: scenario.id,
        updatedAt: new Date().toISOString(),
        initialRisk: {
          likelihood:
            residualRiskData.initialRisk?.likelihood ||
            scenario.operationalScenarios?.[0]?.scenarioVraisemblance ||
            2,
          gravity:
            residualRiskData.initialRisk?.gravity || scenario.gravity || 2,
          riskScore:
            residualRiskData.initialRisk?.riskScore ||
            (scenario.operationalScenarios?.[0]?.scenarioVraisemblance || 2) *
              (scenario.gravity || 2),
          description:
            residualRiskData.initialRisk?.description ||
            `Risque initial du sc\u00e9nario "${scenario.name || scenario.referenceCode}"`,
        },
        residualRisk: {
          likelihood:
            residualRiskData.residualRisk?.likelihood ||
            residualRiskData.residualLikelihood ||
            2,
          gravity:
            residualRiskData.residualRisk?.gravity ||
            residualRiskData.residualGravity ||
            2,
          riskScore:
            residualRiskData.residualRisk?.riskScore ||
            residualRiskData.residualRiskLevel ||
            4,
          reformulation:
            residualRiskData.residualRisk?.reformulation ||
            `Risque r\u00e9siduel apr\u00e8s application des mesures pour le sc\u00e9nario "${scenario.name || scenario.referenceCode}"`,
          residualRiskExpression: residualRiskData.residualRiskExpression || '',
        },
        riskReduction: {
          likelihoodReduction: residualRiskData.riskReduction?.likelihoodReduction || 0,
          gravityReduction: residualRiskData.riskReduction?.gravityReduction || 0,
          overallReduction:
            residualRiskData.riskReduction?.overallReduction ||
            residualRiskData.effectiveness?.final ||
            0,
        },
        measuresApplied: residualRiskData.measuresApplied || [],
        acceptanceStatus: residualRiskData.acceptanceStatus || 'needs_further_treatment',
        acceptanceJustification: residualRiskData.acceptanceJustification || '',
      };

      set((state) => {
        if (!state.synthesisData) return state;

        const updatedStrategicScenarios = state.synthesisData.strategicScenarios.map((s) => {
          if (s.id === scenario.id) {
            return {
              ...s,
              residualRisk,
            };
          }
          return s;
        });

        return {
          synthesisData: {
            ...state.synthesisData,
            strategicScenarios: updatedStrategicScenarios,
          },
          hasUserModified: state.isDataLoaded ? true : state.hasUserModified,
        };
      });

      return { success: true, data: residualRisk };
    },

    updateMeasurePosition: (scenario, measureId, newPosition, temporalPosition, measureType = 'custom') => {
      set((state) => {
        if (!state.synthesisData) return state;

        const updatedStrategicScenarios = state.synthesisData.strategicScenarios.map((s) => {
          if (s.id === scenario.id) {
            const updatedScenario = { ...s };

            switch (measureType) {
              case 'custom':
                updatedScenario.customMeasures = (s.customMeasures || []).map((measure) =>
                  String(measure.id) === String(measureId)
                    ? { ...measure, position: newPosition, temporalPosition }
                    : measure,
                );
                break;
              case 'atelier1':
              case 'dreadedEvent':
                updatedScenario.dreadedEventMeasures = (s.dreadedEventMeasures || []).map((measure) =>
                  String(measure.id) === String(measureId)
                    ? { ...measure, position: newPosition, temporalPosition }
                    : measure,
                );
                break;
              case 'ecosystem':
                updatedScenario.ecosystemMeasures = (s.ecosystemMeasures || []).map((measure) =>
                  String(measure.id) === String(measureId)
                    ? { ...measure, position: newPosition, temporalPosition }
                    : measure,
                );
                break;
              default:
                break;
            }

            return updatedScenario;
          }
          return s;
        });

        return {
          synthesisData: {
            ...state.synthesisData,
            strategicScenarios: updatedStrategicScenarios,
          },
          hasUserModified: state.isDataLoaded ? true : state.hasUserModified,
        };
      });
    },

    updateMeasureField: (strategicScenarioId, measureId, field, value) => {
      set((state) => {
        if (!state.synthesisData) return state;

        const updatedStrategicScenarios = state.synthesisData.strategicScenarios.map((s) => {
          if (s.id !== strategicScenarioId) return s;

          const measureArrays = ['customMeasures', 'dreadedEventMeasures', 'ecosystemMeasures'];
          let found = false;
          const updated = { ...s };

          for (const key of measureArrays) {
            const arr = s[key] || [];
            const idx = arr.findIndex((m) => String(m.id) === String(measureId));
            if (idx !== -1) {
              updated[key] = [
                ...arr.slice(0, idx),
                { ...arr[idx], [field]: value, updatedAt: new Date().toISOString() },
                ...arr.slice(idx + 1),
              ];
              found = true;
              break;
            }
          }

          return found ? updated : s;
        });

        return {
          synthesisData: { ...state.synthesisData, strategicScenarios: updatedStrategicScenarios },
          hasUserModified: state.isDataLoaded ? true : state.hasUserModified,
        };
      });
    },

    removeMeasure: (strategicScenarioId, measureId, arrayKey) => {
      set((state) => {
        if (!state.synthesisData) return state;
        const updatedStrategicScenarios = state.synthesisData.strategicScenarios.map((s) => {
          if (s.id !== strategicScenarioId) return s;
          const arr = s[arrayKey] || [];
          const idx = arr.findIndex((m) => String(m.id) === String(measureId));
          if (idx === -1) return s;
          return { ...s, [arrayKey]: [...arr.slice(0, idx), ...arr.slice(idx + 1)] };
        });
        return {
          synthesisData: { ...state.synthesisData, strategicScenarios: updatedStrategicScenarios },
          hasUserModified: state.isDataLoaded ? true : state.hasUserModified,
        };
      });
    },

    setViewMode: (viewMode) => {
      set({ viewMode });
    },

    loadDashboardData: async (analysisId) => {
      if (!analysisId) return;
      set({ analysisId });
      try {
        const response = await dashboardService.getWorkshop5DashboardData(analysisId);
        if (response.success) {
          if (analysisId !== get().analysisId) return;
          set({ dashboardData: response.data });
        } else {
          if (analysisId !== get().analysisId) return;
          set({ dashboardData: null });
        }
      } catch (error) {
        console.error('Error loading Workshop 5 dashboard data:', error);
        if (analysisId !== get().analysisId) return;
        set({ dashboardData: null });
      }
    },

    resetState: () => {
      set({
        synthesisData: null,
        isLoading: false,
        error: null,
        isDataLoaded: false,
        hasUserModified: false,
        lastUpdated: null,
        dashboardData: null,
        analysisId: null,
      });
    },
  })),
);

export default useAtelier5Store;